import { useState, useEffect } from 'react';
import { Button, message, Upload, Modal, Input, Cascader, Radio } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import type { DefaultOptionType } from 'antd/es/cascader';
import Https from '@/https';
import { getUploadConfig } from '@/https/upload';
import './Device.css';

export default function DeviceCreate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [messageApi, messageContextHolder] = message.useMessage();
  const UploadConfig = getUploadConfig();

  const back = () => {
    navigate(-1);
    dispatch({
      type: 'POP_MENU_KEYPATH',
      payload: { keyPath: 1, selectedKeys: 1 }
    })
  }

  const [loadingFront, setLoadingFront] = useState(false);
  const [frontUrl, setFrontUrl] = useState<string>();
  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      messageApi.error('只能上传JPG/PNG文件!');
    }
    const isLt20M = file.size / 1024 / 1024 < 20;
    if (!isLt20M) {
      messageApi.error('图片大小不能超过20MB!');
    }
    return isJpgOrPng && isLt20M;
  };

  const handleFrontChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setLoadingFront(true);
      return;
    }
    if (info.file.status === 'done') {
      if(info.fileList[0].response) {
        setFrontUrl(info.fileList[0].response[0]);
        setLoadingFront(false);
      }
    }
  };

  const handleFrontRemove = () => {
    setFrontUrl('');
    setLoadingFront(false);
  }

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const handleCancel = () => setPreviewOpen(false);

  const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  const [name, setName] = useState('');
  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }

  const [sn, setSn] = useState('');
  const onSnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSn(e.target.value);
  }

  const [number, setNumber] = useState('');
  const onNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNumber(e.target.value);
  }

  const [deviceType, setDeviceType] = useState<string[]>([]);
  const [deviceTypeOptions, setDeviceTypeOptions] = useState<DefaultOptionType[]>([]);
  const getDeviceTypeOptions = () => {
    Https.post({
      url: 'Api/device/V1/deviceEnum/FindDeviceTypeEnumList',
      data: {}
    }).then((res) => {
      if(res.state === 1) {
        let list = res.data;
        getModelOptions(list);
      }
    })
  }

  const getModelOptions = (deviceType: DefaultOptionType[]) => {
    Https.post({
      url: 'Api/device/V1/DeviceModel/List/By',
      data: {}
    }).then((res) => {
      if(res.state === 1) {
        let deviceTypeList = deviceType.map(item => {
          return {
            value: item.value,
            label: item.text,
            children: []
          }
        })
        let list = res.data.deviceModels;
        for(let item of list) {
          for(let type of deviceTypeList) {
            if(type.value === item.deviceType && item.isActive) {
              (type.children as any[]).push({
                value: item.modelId,
                label: item.name
              });
            }
          }
        }
        setDeviceTypeOptions(deviceTypeList);
      }
    })
  }

  const deviceTypeChange = (value: string[]) => {
    setDeviceType(value);
  }

  const [sim, setSim] = useState('');
  const onSimChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSim(e.target.value);
  }

  const [softwareVersion, setSoftwareVersion] = useState('');
  const onSoftwareVersionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSoftwareVersion(e.target.value);
  }

  const [status, setStatus] = useState(3);

  const submit = () => {
    if(!name) {
      messageApi.error('设备名称不能为空');
      return;
    }
    if(!sn) {
      messageApi.error('设备唯一码不能为空');
      return;
    }
    if(deviceType.length < 2) {
      messageApi.error('设备类型和型号不能为空');
      return;
    }
    if(!number) {
      messageApi.error('第三方唯一码不能为空');
      return;
    }
    let params: {
      name: string;
      sn: string;
      deviceType: string;
      number: string;
      sim?: string;
      softwareVersion?: string;
      status: number;
      frontUrl?: string;
      modelId?: string;
    } = {
      name: name,
      sn: sn,
      deviceType: deviceType[0],
      number: number,
      sim: sim,
      softwareVersion: softwareVersion,
      status: status,
      frontUrl: frontUrl
    }
    if(deviceType.length > 1) {
      params.modelId = deviceType[1];
    }
    Https.post({
      url: 'Api/device/V1/device/AddDevice',
      data: params,
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('添加成功');
        setTimeout(() => {
          back()
        }, 1000);
      } else {
        messageApi.error(res.data.errorMessage || res.errorMessage);
      }
    })
  }

  useEffect(() => {
    getDeviceTypeOptions();
  }, [])

  return (
    <section className="Device-Create">
      <Button onClick={back} size="small" className="back-btn">返回</Button>
      <section className="create-box">
        <section className="create-img">
          <header>场景照片</header>
            <Upload
              name="file"
              listType="picture-card"
              maxCount={1}
              className="avatar-uploader"
              showUploadList={true}
              action={UploadConfig.action}
              headers={UploadConfig.headers}
              beforeUpload={beforeUpload}
              onChange={handleFrontChange}
              onRemove={handleFrontRemove}
              onPreview={handlePreview}>
              { frontUrl ? null :
                <section>
                  {loadingFront ? <LoadingOutlined /> : <PlusOutlined />}
                  <section style={{ marginTop: 8 }}>上传图片</section>
                </section>
              }
            </Upload>
        </section>
        <section className="create-info">
          <section className="create-info-item">
            <header>
              <span className="label required">设备名称</span>
            </header>
            <section className="input">
              <Input placeholder="请输入设备名称" value={name} allowClear onChange={onNameChange} />
            </section>
          </section>
          <section className="create-info-item">
            <header>
              <span className="label required">设备唯一码</span>
            </header>
            <section className="input">
              <Input placeholder="请输入设备唯一码" value={sn} allowClear onChange={onSnChange} />
            </section>
          </section>
          <section className="create-info-item">
            <header>
              <span className="label required">第三方唯一码</span>
            </header>
            <section className="input">
              <Input placeholder="请输入第三方唯一码" value={number} allowClear onChange={onNumberChange} />
            </section>
          </section>
          <section className="create-info-item create-info-width-item">
            <header>
              <span className="label required">类型型号</span>
            </header>
            <section>
              <Cascader
                allowClear
                value={deviceType}
                options={deviceTypeOptions}
                onChange={(value) => deviceTypeChange(value as string[])}
                style={{ width: 200 }}
                placeholder="请选择设备型号"
              />
            </section>
          </section>
          <section className="create-info-item">
            <header>
              <span className="label">设备sim码</span>
            </header>
            <section className="input">
              <Input placeholder="请输入设备sim码" value={sim} allowClear onChange={onSimChange} />
            </section>
          </section>
          <section className="create-info-item">
            <header>
              <span className="label">版本号</span>
            </header>
            <section className="input">
              <Input placeholder="请输入版本号" value={softwareVersion} allowClear onChange={onSoftwareVersionChange} />
            </section>
          </section>
          <section className="create-info-item">
            <header>
              <span className="label">设备状态</span>
            </header>
            <section>
              <Radio.Group
                value={status}
                options={[
                  { value: 1, label: '使用中' },
                  { value: 2, label: '暂停' },
                  { value: 3, label: '预投放' },
                ]}
                onChange={(e) => setStatus(e.target.value)}
              />
            </section>
          </section>
        </section>
      </section>
      <section className="create-btn">
          <Button onClick={submit} type="primary">立即创建</Button>
        </section>
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
        <img alt="example" style={{ width: '100%' }} src={previewImage} />
      </Modal>
      { messageContextHolder }
    </section>
  )
}